import {
  type RefObject,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState
} from "react"

// Sized to the space left below it, because a section can sit in a clipping box
// that is itself height-bounded — there a taller table is cut off rather than
// scrolled to, along with anything the wrapper puts after it. `minRows` and
// `fillsViewportBelow` trade some of that caution away, each on its own terms.
const BOTTOM_GUTTER = 24
const MIN_HEIGHT = 120
// Resizing the body can move what sits above it by a pixel or two — a scrollbar
// column appears, a header row re-wraps. Without a dead band those nudges feed
// back into the next measurement and the table oscillates forever.
const TOLERANCE = 8
// Stands in for the first render only, before the body can be measured. A
// caller that lifts `max` to opt out of the cap still starts somewhere sane.
const INITIAL_HEIGHT = 600

/**
 * The CSS-imposed ceiling on an ancestor's own box, in pixels — not how tall
 * it currently renders. `getComputedStyle` resolves a `calc()`/`vh` bound to
 * a concrete px value, same as a literal `500px` would.
 *
 * `max-height` only, never `height`: an unset `height` still resolves to a
 * concrete px number here — the browser reports the box's current rendered
 * height, not the keyword `auto` — so treating it as a bound would just
 * reintroduce the content-chasing circularity this function exists to avoid.
 * `max-height` is the one property that reliably reports `none` when nothing
 * authored it.
 */
function cssHeightBound(style: CSSStyleDeclaration): number | null {
  const value = style.maxHeight
  if (!value || value === "none") return null
  const px = parseFloat(value)
  return Number.isNaN(px) ? null : px
}

/**
 * Bottom edge the table may actually reach.
 *
 * Not always the viewport's: a page shell can scroll a column of its own —
 * `FixedContainer` caps at `100vh - 88px` and, by default, keeps `pb-24` free
 * below its last card so the support widget cannot sit on it. Measured against
 * the window, the body is sized past that edge, and what the wrapper renders
 * under it (the result count) ends up below the fold of a scrollport nobody
 * expects to scroll.
 *
 * Only a real scrollport counts, and "real" means CSS-bounded (a `max-height`
 * or `height` other than `auto`/`none`) — not just `overflow-y: auto`. An
 * `overflow-auto` ancestor with no such bound only ever grows to fit what's
 * inside it, table included: reading its rendered `bottom` would feed the
 * table's own height back into the space being measured for it, so a page
 * with too little data to fill the viewport converges on the hook's own
 * minimum instead of leaving the rest of the page blank. (Same trap the
 * `overflow: hidden` case below already knew to avoid — this just covers the
 * `auto`/`scroll` ancestor that looked bounded but wasn't.) When the bound is
 * real, the ceiling comes from the ancestor's own top plus that bound, not its
 * current `bottom` — the same content-chasing problem, one layer up.
 */
function reachableBottom(container: Element): number {
  let node = container.parentElement

  while (node && node !== document.body) {
    const style = getComputedStyle(node)
    if (style.overflowY === "auto" || style.overflowY === "scroll") {
      const bound = cssHeightBound(style)
      if (bound != null) {
        const padding = parseFloat(style.paddingBottom) || 0
        const border = parseFloat(style.borderBottomWidth) || 0
        return node.getBoundingClientRect().top + bound - padding - border
      }
    }
    node = node.parentElement
  }

  return window.innerHeight
}

/** What is free under the body where it currently sits. */
function spaceLeftBelow(container: Element, top: number, reserve: number) {
  const bottom = Math.min(reachableBottom(container), window.innerHeight)
  return bottom - top - reserve - BOTTOM_GUTTER
}

/**
 * What the body gets when the card is the screen, rather than a tenant of it.
 *
 * Everything the card carries travels with it when it is scrolled to, so both
 * ends come off the viewport: the chrome above the body — its filters as much
 * as the table header — and whatever the card renders under the measured
 * block. The floating "+" is not part of that: its anchor has no height, so it
 * is only ever painted over the card.
 */
function spaceOnItsOwnScreen(
  container: Element,
  top: number,
  reserve: number,
  stickyChrome: number
) {
  const card = container.closest(".ant-card") ?? container
  const box = card.getBoundingClientRect()
  const above = top - box.top
  const below = box.bottom - container.getBoundingClientRect().bottom

  return (
    window.innerHeight - stickyChrome - above - reserve - below - BOTTOM_GUTTER
  )
}

/**
 * Height the body needs before `minRows` of its rows are actually on screen.
 *
 * The rows are summed rather than multiplied by a representative one: a
 * description that wraps makes its row half as tall again as its neighbours, so
 * any single row stands in badly for the others — and picking the first one
 * hands the count to whichever milestone happens to sort first. Fewer rows than
 * asked for is every row there is.
 *
 * The horizontal scrollbar is added back because `scroll.y` lands as a
 * `max-height` on the same box that carries `overflow-x`, so on a platform that
 * gives that scrollbar real height it comes out of the rows.
 */
function heightOfRows(container: Element, rows: number): number {
  const bodyRows = container.querySelectorAll(".ant-table-row")

  let total = 0
  for (let i = 0; i < Math.min(rows, bodyRows.length); i++) {
    total += bodyRows[i].getBoundingClientRect().height
  }

  const body = container.querySelector<HTMLElement>(".ant-table-body")
  const scrollbar = body ? body.offsetHeight - body.clientHeight : 0

  return Math.ceil(total + scrollbar)
}

interface FittedTableHeightOptions<T extends HTMLElement> {
  /** Space to keep free below the table body, e.g. a floating add control. */
  reserve?: number
  max?: number
  /**
   * Container to measure within, when the caller already holds a ref to it —
   * e.g. because it also measures what the container puts below the body.
   */
  containerRef?: RefObject<T | null>
  /**
   * Size the body for the screen the card gets once it has been scrolled to,
   * leaving this many pixels of sticky chrome above it — rather than for the
   * gap left under whatever currently sits on top of the page.
   *
   * Only for a page whose content above the card is worth scrolling past and
   * which can actually scroll that far: the body then exceeds the space free
   * on first paint, and it is the page scroll that makes up the difference. On
   * a card inside a bounded scrollport this would size the body past an edge
   * nothing can scroll to, so it stays opt-in.
   */
  fillsViewportBelow?: number
  /**
   * Never leave the body too short to show this many rows, whatever the space
   * below it comes to.
   *
   * For a list whose container renders something that comes and goes under the
   * body — a selection toolbar — because that arrives as `reserve` and is taken
   * straight off the body.
   *
   * It buys rows only as far as the reader can see: the body grows downward,
   * and a sticky footer stops at its scrollport's content edge, so rows past
   * that edge end up behind it. Worth it where the shortfall is a row or two,
   * not as a way to hold a table open in a space that cannot show it.
   */
  minRows?: number
}

/**
 * Height for a table's `scroll.y`, measured from where its body actually sits
 * rather than from a per-table guess at how much chrome is above it. For
 * sections that end at the viewport — not ones that scroll a taller page,
 * unless `fillsViewportBelow` says the page is meant to scroll.
 */
export function useFittedTableHeight<T extends HTMLElement = HTMLDivElement>({
  reserve = 0,
  max = 600,
  containerRef,
  fillsViewportBelow,
  minRows
}: FittedTableHeightOptions<T> = {}) {
  const ownRef = useRef<T>(null)
  const ref = containerRef ?? ownRef
  const [height, setHeight] = useState(Math.min(max, INITIAL_HEIGHT))

  const measure = useCallback(() => {
    const container = ref.current
    if (!container) return

    const body = container.querySelector(".ant-table-body") ?? container
    const { top } = body.getBoundingClientRect()

    // Re-read every time rather than caching: a column shown, hidden or
    // dragged narrower re-wraps its cells without remounting the table. Rows
    // keep their height when the body is resized — `scroll.y` makes the body a
    // scrollport, it does not squeeze what is inside it — so this cannot feed
    // back the way the measurements `TOLERANCE` damps do.
    const floor = Math.max(
      MIN_HEIGHT,
      minRows == null ? 0 : heightOfRows(container, minRows)
    )

    const available =
      fillsViewportBelow == null
        ? spaceLeftBelow(container, top, reserve)
        : spaceOnItsOwnScreen(container, top, reserve, fillsViewportBelow)

    // `max` outermost: a floor that beats the cap would leave `max` meaning
    // nothing for the caller that set both.
    const next = Math.round(Math.min(max, Math.max(floor, available)))

    setHeight((current) =>
      // The dead band is for the pixel or two a reflow moves things by. A body
      // under the floor is not that, and the floor arrives a render late —
      // rows render after the height first settles — so absorbing that step
      // would strand the table under its own guarantee for good.
      current < Math.min(floor, max) || Math.abs(current - next) > TOLERANCE
        ? next
        : current
    )
  }, [ref, reserve, max, fillsViewportBelow, minRows])

  // Every render, not just on mount: the chrome above the body grows when
  // filter options arrive or a header wraps, which moves the body down without
  // resizing anything this hook can observe.
  useLayoutEffect(measure)

  useEffect(() => {
    window.addEventListener("resize", measure)

    const observer =
      typeof ResizeObserver === "undefined" || !ref.current
        ? null
        : new ResizeObserver(measure)
    observer?.observe(ref.current as Element)

    return () => {
      window.removeEventListener("resize", measure)
      observer?.disconnect()
    }
  }, [measure])

  return { ref, height }
}
